/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import {
  useFloating,
  useHover,
  useInteractions,
  offset,
  shift,
  autoUpdate,
  FloatingPortal,
} from "@floating-ui/react";
import styled from "styled-components";
import SntAddFilterIcon from "@icons/SntAddFilterIcon";
import SntRemoveFilterIcon from "@icons/SntRemoveFilterIcon";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import { getCellFilter } from "../../../utils/cellUtils";

interface CellFilterProps {
  cell: any;
  row: any;
  isDisabbledAddFilter: boolean;
  restrictedFilters?: string[];
  filterVisibleKeys?: string[];
  filterMap?: Record<string, unknown>;
  onFilterClicked?: (
    action: string,
    columnKey: string,
    filterKeys: string[],
    cellValue: any[] | null
  ) => void;
  className?: string;
  children: React.ReactNode;
}

const FilterOverlay = styled.div`
  display: inline-flex;
  align-items: center;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  padding: 0.125rem 0.25rem;
  touch-action: none;
  cursor: pointer !important;

  svg {
    &:hover {
      color: #0071a1;
    }
  }
`;

export const CellFilter = ({
  cell,
  row,
  isDisabbledAddFilter,
  restrictedFilters,
  filterVisibleKeys,
  filterMap = {},
  onFilterClicked,
  className,
  children,
}: CellFilterProps) => {
  const [open, setOpen] = useState(false);

  const filterKey = cell.column.filterKeys?.[0];
  const showAddFilter = !filterVisibleKeys?.includes(filterKey);
  const showRemoveFilter = !filterMap || filterMap[filterKey] !== "MULTI_STRING";

  const shouldShow =
    !isDisabbledAddFilter &&
    cell.column.filterKeys?.length > 0 &&
    (!restrictedFilters ||
      restrictedFilters.length === 0 ||
      !restrictedFilters?.includes(filterKey)) &&
    (showAddFilter || showRemoveFilter);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "right",
    middleware: [
      // Pull the overlay back inside the cell so it hugs the right edge
      offset(({ rects }) => -rects.floating.width - 4),
      shift({ padding: 5 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    enabled: shouldShow,
    delay: { open: 150, close: 50 },
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const handleFilterForValue = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const cellValue = getCellFilter(cell.column, row.original);

    onFilterClicked?.(
      "FILTER_FOR_VALUE",
      cell.column.key,
      cell.column.filterKeys,
      cellValue?.length > 0 ? cellValue : null
    );
  };

  const handleFilterOutValue = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const cellValue = getCellFilter(cell.column, row.original);
    onFilterClicked?.(
      "FILTER_OUT_VALUE",
      cell.column.key,
      cell.column.filterKeys,
      cellValue?.length > 0 ? cellValue : null
    );
  };

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className={`w-100 ${className || ""}`}
      >
        {children}
      </div>
      {shouldShow && open && (
        <FloatingPortal>
          <FilterOverlay
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 9999 }}
            {...getFloatingProps()}
            className="cell-filter"
          >
            {showAddFilter && (
              <SntAddFilterIcon
                className={showRemoveFilter ? "me-1" : ""}
                title={translate(LanguageKey.filter_for_value_key)}
                onClick={handleFilterForValue}
              />
            )}
            {showRemoveFilter && (
              <SntRemoveFilterIcon
                title={translate(LanguageKey.filter_out_value_key)}
                onClick={handleFilterOutValue}
              />
            )}
          </FilterOverlay>
        </FloatingPortal>
      )}
    </>
  );
};
